import { useCallback, useEffect, useMemo, useState } from "react";
import CollectionFilter from "../../components/CollectionFilter/CollectionFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  getExploreCollections,
  getUserFavorites,
  getUserFollowing,
} from "../../store/slices/model/asyncThunks";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  selectCategories,
  selectExploreCollections,
  selectExploreCollectionsFilter,
  selectExploreCollectionsKeyword,
  selectExploreCollectionsPage,
  selectExploreCollectionsSearch,
  selectFavorites,
  selectFavoritesPage,
  selectFollowing,
  selectFollowingPage,
  selectSelectedCollection,
  setSelectedCollection,
  setSelectedExploreCollectionFilter,
  setSelectedExploreCollectionKeyword,
  setSelectedExploreCollectionSearch,
} from "../../store/slices/model/slice";
import Pagination from "../../components/Pagination/Pagination";
import {
  Button,
  Column,
  Container,
  Content,
  ContentContainer,
  EmptyState,
  PaginationWrapper,
} from "./HomePage.styles";
import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import ExploreItem from "../../components/ExploreItem/ExploreItem";
import { useMediaQuery } from "react-responsive";

import { selectLoggedIn } from "../../store/slices/auth/slice";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import { selectUser } from "../../store/slices/user/slice";
import {
  BLEND_MODEL,
  CREATE_IMAGE_MODEL_NAME,
  PREMIUM_PLAN_NAME,
  PRO_PLAN_NAME,
} from "../../utils/constants";
import EditImageModal from "../../components/EditImageModal/EditImageModal";
import { isImageOutput } from "../../utils/utils";
import CreateImageModal from "../../components/CreateImageModal/CreateImageModal";
import GroupModal from "../../components/GroupModal/GroupModal";
import PlayboxAdCard, {
  PLAYBOX_AD_ROTATION,
} from "../../components/PlayboxAdCard/PlayboxAdCard";

const EXPLORE_FETCH_RETRY_COUNT = 2;
const EXPLORE_FETCH_RETRY_DELAY_MS = 2000;
const AD_FEED_INTERVAL = 8;
const MAX_ADS_PER_PAGE = 4;
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const HomePage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageInStore = useSelector(selectExploreCollectionsPage);
  const pageInStoreFavorite = useSelector(selectFavoritesPage);
  const pageInStoreFollowing = useSelector(selectFollowingPage);
  const page = Number(searchParams.get("page")) || pageInStore || 1;

  const selectedCollection = useSelector(selectSelectedCollection);

  const filter = useSelector(selectExploreCollectionsFilter);
  const savedSearch = useSelector(selectExploreCollectionsSearch);
  const savedKeyword = useSelector(selectExploreCollectionsKeyword);

  const [searchValue, setSearchValue] = useState(() => savedSearch ?? "");
  const [search, setSearch] = useState(savedSearch ?? "");

  const exploreData = useSelector(selectExploreCollections);
  const favoriteData = useSelector(selectFavorites);
  const followingData = useSelector(selectFollowing);
  const loggedIn = useSelector(selectLoggedIn);
  const { data: categories, error: errorCategories } =
    useSelector(selectCategories);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isCreateImageModalOpen, setIsCreateImageModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const error = exploreData.error || errorCategories;

  const user = useSelector(selectUser);

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const fetchExploreCollectionsWithRetry = useCallback(
    async (params) => {
      for (
        let attempt = 0;
        attempt <= EXPLORE_FETCH_RETRY_COUNT;
        attempt += 1
      ) {
        try {
          return await dispatch(getExploreCollections(params)).unwrap();
        } catch (error) {
          if (attempt === EXPLORE_FETCH_RETRY_COUNT) {
            throw error;
          }

          await pause(EXPLORE_FETCH_RETRY_DELAY_MS);
        }
      }

      return null;
    },
    [dispatch],
  );

  const requestExploreCollections = useCallback(
    (params) => {
      void fetchExploreCollectionsWithRetry(params).catch(() => {});
    },
    [fetchExploreCollectionsWithRetry],
  );

  useEffect(() => {
    const isFavorites = filter === "FAVORITES";
    const isFollowing = filter === "FOLLOWING";
    const isExplore = !isFavorites && !isFollowing;

    if (
      !Number(searchParams.get("page")) &&
      ((isExplore && pageInStore) ||
        (isFavorites && pageInStoreFavorite) ||
        (isFollowing && pageInStoreFollowing))
    ) {
      if (isExplore && pageInStore && pageInStore > 1) {
        setSearchParams({ page: pageInStore });
      }
      if (isFavorites && pageInStoreFavorite && pageInStoreFavorite > 1) {
        setSearchParams({ page: pageInStoreFavorite });
      }
      if (isFollowing && pageInStoreFollowing && pageInStoreFollowing > 1) {
        setSearchParams({ page: pageInStoreFollowing });
      }
    }
  }, [
    filter,
    pageInStore,
    pageInStoreFavorite,
    pageInStoreFollowing,
    searchParams,
    setSearchParams,
  ]);

  //   useEffect(() => {
  //     if (!filter) {
  //       setSearchParams({ page: 1 });
  //     }
  //   }, [filter, setSearchParams]);

  useEffect(() => {
    if (categories?.length && !filter) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
    if (!categories?.length) {
      const keyword = searchParams.get("category");
      if (keyword) {
        dispatch(setSelectedExploreCollectionKeyword(keyword));
        searchParams.delete("category");
        setSearchParams(searchParams);
      }

      requestExploreCollections({
        page: page || 1,
        search: "",
        ...(keyword && { keyword }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categories,
    dispatch,
    filter,
    page,
    requestExploreCollections,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (
      !loggedIn &&
      (filter === "FAVORITES" || filter === "FOLLOWING") &&
      categories.length
    ) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
  }, [loggedIn, filter, categories, dispatch]);

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  const data = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.data;
    } else if (filter === "FOLLOWING") {
      return followingData.data;
    } else {
      return exploreData.data;
    }
  }, [filter, favoriteData, followingData, exploreData]);

  const feedItems = useMemo(() => {
    if (!data?.length) {
      return data ?? [];
    }

    const withAds = [];
    let adCount = 0;
    data.forEach((item) => {
      withAds.push(item);
      if (
        adCount < MAX_ADS_PER_PAGE &&
        (withAds.length + 1) % AD_FEED_INTERVAL === 0
      ) {
        const ad = PLAYBOX_AD_ROTATION[adCount % PLAYBOX_AD_ROTATION.length];
        withAds.push({
          _id: `playbox-ad-${withAds.length + 1}`,
          isPlayboxAd: true,
          adVariant: ad.variant,
          adCreative: ad.creative,
        });
        adCount += 1;
      }
    });
    return withAds;
  }, [data]);

  const total = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.total;
    } else if (filter === "FOLLOWING") {
      return followingData.total;
    } else {
      return exploreData.total;
    }
  }, [filter, favoriteData, followingData, exploreData]);

  const loading = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.loading;
    } else if (filter === "FOLLOWING") {
      return followingData.loading;
    } else {
      return exploreData.loading;
    }
  }, [filter, favoriteData, followingData, exploreData]);

  const searchDisabled = loggedIn === false;
  const activeSearchValue = searchDisabled ? "" : searchValue;

  useEffect(() => {
    if (!searchDisabled) return;

    if (searchValue) {
      setSearchValue("");
    }

    if (search) {
      setSearch("");
    }

    if (savedSearch) {
      dispatch(setSelectedExploreCollectionSearch(""));
    }
  }, [dispatch, savedSearch, search, searchDisabled, searchValue]);

  const changeHandler = useCallback(
    (text) => {
      if (searchDisabled) return;

      searchParams.delete("page");
      setSearchParams(searchParams);
      if (search !== text) {
        setSearch(text);
        dispatch(setSelectedExploreCollectionSearch(text));
        if (filter === "FAVORITES") {
          dispatch(
            getUserFavorites({ page: 1, search: text, keyword: savedKeyword }),
          );
        } else if (filter === "FOLLOWING") {
          dispatch(
            getUserFollowing({ page: 1, search: text, keyword: savedKeyword }),
          );
        } else {
          requestExploreCollections({
            page: 1,
            search: text,
            categoryId: filter,
            keyword: savedKeyword,
          });
        }
      }
    },
    [
      searchParams,
      setSearchParams,
      search,
      dispatch,
      filter,
      savedKeyword,
      requestExploreCollections,
      searchDisabled,
    ],
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler],
  );

  useEffect(() => {
    return () => debouncedChangeHandler.cancel();
  }, [debouncedChangeHandler]);

  const setPage = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    if (page > 1) {
      searchParams.set("page", page);
    } else {
      searchParams.delete("page");
    }

    setSearchParams(searchParams);
    if (filter === "FAVORITES") {
      dispatch(
        getUserFavorites({
          page,
          search: activeSearchValue,
          keyword: savedKeyword,
        }),
      );
    } else if (filter === "FOLLOWING") {
      dispatch(
        getUserFollowing({
          page,
          search: activeSearchValue,
          keyword: savedKeyword,
        }),
      );
    } else {
      requestExploreCollections({
        page,
        search: activeSearchValue,
        categoryId: filter,
        keyword: savedKeyword,
      });
    }
  };

  useEffect(() => {
    if (filter === "FAVORITES" && !data) {
      searchParams.delete("page");
      setSearchParams(searchParams);
      dispatch(
        getUserFavorites({
          page: 1,
          search: "",
          keyword: savedKeyword,
        }),
      );
    }
  }, [filter, dispatch, data, searchParams, setSearchParams, savedKeyword]);

  useEffect(() => {
    if (filter === "FOLLOWING" && !data) {
      searchParams.delete("page");
      setSearchParams(searchParams);
      dispatch(
        getUserFollowing({
          page: 1,
          search: "",
          keyword: savedKeyword,
        }),
      );
    }
  }, [filter, dispatch, data, searchParams, setSearchParams, savedKeyword]);

  useEffect(() => {
    if (!filter || data) return;

    if (filter !== "FAVORITES" && filter !== "FOLLOWING") {
      searchParams.delete("page");
      setSearchParams(searchParams);

      requestExploreCollections({
        page: 1,
        search: "",
        categoryId: filter,
        keyword: savedKeyword,
      });
    }
  }, [
    dispatch,
    filter,
    data,
    searchParams,
    setSearchParams,
    savedKeyword,
    requestExploreCollections,
  ]);

  const onChangeFilter = (filter) => {
    dispatch(setSelectedExploreCollectionFilter(filter));
    searchParams.delete("page");
    setSearchParams(searchParams);

    if (filter === "FAVORITES") {
      dispatch(
        getUserFavorites({
          page: 1,
          search: activeSearchValue,
          keyword: savedKeyword,
        }),
      );
    } else if (filter === "FOLLOWING") {
      dispatch(
        getUserFollowing({
          page: 1,
          search: activeSearchValue,
          keyword: savedKeyword,
        }),
      );
    } else {
      requestExploreCollections({
        page: 1,
        search: activeSearchValue,
        categoryId: filter,
        keyword: savedKeyword,
      });
    }
  };

  const onChangeKeyword = (keyword) => {
    dispatch(setSelectedExploreCollectionKeyword(keyword));
    searchParams.delete("page");
    setSearchParams(searchParams);
    if (filter === "FAVORITES") {
      dispatch(
        getUserFavorites({
          page: 1,
          search: activeSearchValue,
          keyword,
        }),
      );
    } else if (filter === "FOLLOWING") {
      dispatch(
        getUserFollowing({
          page: 1,
          search: activeSearchValue,
          keyword,
        }),
      );
    } else {
      requestExploreCollections({
        page: 1,
        search: activeSearchValue,
        categoryId: filter,
        keyword,
      });
    }
  };

  return (
    <>
      <Container>
        <CollectionFilter
          loading={loading}
          searchPlaceholderText="title, username or tags"
          filter={filter}
          setFilter={onChangeFilter}
          searchValue={searchValue}
          onSearchChange={(value) => {
            if (searchDisabled) return;
            setSearchValue(value);
            debouncedChangeHandler(value);
          }}
          disableSearch={searchDisabled}
          onDisabledSearchClick={() => dispatch(setSignUpModalOpen(true))}
          onChangeKeyword={onChangeKeyword}
          isProEnabled={isProEnabled}
        />
        <ContentContainer>
          {error ? (
            <Content>
              <EmptyState>
                <h3>We’re having trouble loading videos</h3>
                <p>
                  This is usually temporary. Please check your connection and
                  try again.
                </p>
                <Button
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  Retry
                </Button>
              </EmptyState>
            </Content>
          ) : loading || !data ? (
            <VideoMasonrySkeletonLoader count={24} fullWidth mobileTwoRows />
          ) : (
            <Content>
              {!data.length ? (
                <EmptyState>
                  <h3>No videos found</h3>
                  <p>There are no videos to display at the moment.</p>
                </EmptyState>
              ) : (
                Array.from({ length: numOfColumns }).map((_, colIndex) => {
                  return (
                    <Column key={colIndex}>
                      {feedItems
                        .filter((_, i) => i % numOfColumns === colIndex)
                        .map((item) => {
                          if (item.isPlayboxAd) {
                            return (
                              <PlayboxAdCard
                                key={item._id}
                                variant={item.adVariant}
                                creativeIndex={item.adCreative}
                              />
                            );
                          }
                          return (
                            <ExploreItem
                              key={item._id}
                              item={item}
                              onItemClick={() => {
                                if (loggedIn) {
                                  dispatch(setSelectedCollection(item));
                                  if (
                                    item.modelType === CREATE_IMAGE_MODEL_NAME
                                  ) {
                                    setIsCreateImageModalOpen(true);
                                  } else if (item.modelType === BLEND_MODEL) {
                                    setIsGroupModalOpen(true);
                                  } else if (
                                    isImageOutput(
                                      item.modelType ?? item.modelName,
                                    )
                                  ) {
                                    setIsEditImageModalOpen(true);
                                  } else {
                                    setIsGenerateCollectionModalOpen(true);
                                  }
                                } else {
                                  dispatch(setSignUpModalOpen(true));
                                }
                              }}
                            />
                          );
                        })}
                    </Column>
                  );
                })
              )}
            </Content>
          )}
        </ContentContainer>
        <PaginationWrapper>
          <Pagination
            currentPage={page}
            totalCount={total}
            perPage={30}
            onPageChange={(page) => setPage(page)}
          />
        </PaginationWrapper>
      </Container>

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
        onChangeFilter={onChangeFilter}
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollection}
        onClose={() => setIsEditImageModalOpen(false)}
      />

      <CreateImageModal
        isOpen={isCreateImageModalOpen}
        collection={selectedCollection}
        onClose={() => setIsCreateImageModalOpen(false)}
      />

      <GroupModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
      />
    </>
  );
};

export default HomePage;
